/**
 * OutcomeVisualization Component
 * Shows PHQ-9 and GAD-7 progress charts for clients
 * 
 * Features:
 * - Line charts showing score trends over time
 * - Severity zone indicators (minimal, mild, moderate, severe)
 * - Score interpretation
 * - Responsive design
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Activity,
  Brain,
  Heart,
  Calendar,
  Info,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PHQ-9 severity thresholds and colors
const PHQ9_ZONES = [
  { min: 0, max: 4, label: 'Minimal', color: '#22c55e', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { min: 5, max: 9, label: 'Mild', color: '#eab308', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  { min: 10, max: 14, label: 'Moderate', color: '#f97316', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  { min: 15, max: 19, label: 'Moderately Severe', color: '#ef4444', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { min: 20, max: 27, label: 'Severe', color: '#dc2626', bgColor: 'bg-red-200', textColor: 'text-red-800' }
];

// GAD-7 severity thresholds and colors
const GAD7_ZONES = [
  { min: 0, max: 4, label: 'Minimal', color: '#22c55e', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { min: 5, max: 9, label: 'Mild', color: '#eab308', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  { min: 10, max: 14, label: 'Moderate', color: '#f97316', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  { min: 15, max: 21, label: 'Severe', color: '#ef4444', bgColor: 'bg-red-100', textColor: 'text-red-700' }
];

const getSeverityZone = (score, type = 'PHQ-9') => {
  const zones = type === 'PHQ-9' ? PHQ9_ZONES : GAD7_ZONES;
  return zones.find(z => score >= z.min && score <= z.max) || zones[0];
};

const getTrendIcon = (trend) => {
  if (trend > 0) return <TrendingUp className="w-4 h-4 text-red-500" />;
  if (trend < 0) return <TrendingDown className="w-4 h-4 text-green-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
};

const getTrendLabel = (trend, type) => {
  const direction = trend < 0 ? 'improvement' : trend > 0 ? 'increase' : 'no change';
  return `${Math.abs(trend)} point ${direction}`;
};

// Simple Line Chart Component (no external dependencies)
const SimpleLineChart = ({ data, maxScore, zones, title, color }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-400">
        <p>No data available</p>
      </div>
    );
  }

  const padding = 40;
  const width = 100;
  const height = 60;
  
  const minY = 0;
  const maxY = maxScore;
  
  // Calculate points
  const points = data.map((d, i) => {
    const x = padding + (i / Math.max(data.length - 1, 1)) * (width - padding * 2);
    const y = height - padding - ((d.score - minY) / (maxY - minY)) * (height - padding * 2);
    return { x, y, ...d };
  });

  // Create SVG path
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-48" preserveAspectRatio="xMidYMid meet">
        {/* Severity zone backgrounds */}
        {zones.map((zone, i) => {
          const y1 = height - padding - ((zone.max - minY) / (maxY - minY)) * (height - padding * 2);
          const y2 = height - padding - ((zone.min - minY) / (maxY - minY)) * (height - padding * 2);
          const rectHeight = Math.abs(y2 - y1); // Ensure positive height
          const rectY = Math.min(y1, y2); // Use the smaller y value as starting point
          return (
            <rect
              key={i}
              x={padding}
              y={rectY}
              width={width - padding * 2}
              height={rectHeight || 1} // Fallback to 1 if height is 0
              fill={zone.color}
              opacity="0.1"
            />
          );
        })}
        
        {/* Grid lines */}
        {[0, maxScore / 4, maxScore / 2, (maxScore * 3) / 4, maxScore].map((val, i) => {
          const y = height - padding - ((val - minY) / (maxY - minY)) * (height - padding * 2);
          return (
            <g key={i}>
              <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={padding - 3} y={y + 1} textAnchor="end" fontSize="3" fill="#9ca3af">{val}</text>
            </g>
          );
        })}
        
        {/* Line */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        
        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="2" fill="white" stroke={color} strokeWidth="1" />
            {/* Date labels for first, middle, last */}
            {(i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2)) && (
              <text x={p.x} y={height - padding + 8} textAnchor="middle" fontSize="2.5" fill="#6b7280">
                {new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

// Assessment Card Component
const AssessmentCard = ({ type, data, icon: Icon, color, maxScore, zones }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!data || data.length === 0) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Icon className={`w-5 h-5 ${color}`} />
            {type}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-400">
            <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No {type} assessments recorded yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const latestScore = data[0]?.score;
  const previousScore = data[1]?.score;
  const trend = previousScore !== undefined ? latestScore - previousScore : 0;
  const severity = getSeverityZone(latestScore, type);
  
  // Calculate overall trend from first to last
  const overallTrend = data.length > 1 ? data[0].score - data[data.length - 1].score : 0;
  const isImproving = overallTrend < 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Icon className={`w-5 h-5 ${color}`} />
            {type}
          </CardTitle>
          <Badge className={`${severity.bgColor} ${severity.textColor}`}>
            {severity.label}
          </Badge>
        </div>
        <CardDescription>
          {type === 'PHQ-9' ? 'Depression Screening' : 'Anxiety Screening'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Score Display */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm text-gray-500">Current Score</p>
            <p className="text-3xl font-bold" style={{ color: severity.color }}>{latestScore}</p>
            <p className="text-xs text-gray-400">out of {maxScore}</p>
          </div>
          
          {data.length > 1 && (
            <div className="text-right">
              <p className="text-sm text-gray-500">vs Previous</p>
              <div className="flex items-center gap-1 justify-end">
                {getTrendIcon(trend)}
                <span className={trend < 0 ? 'text-green-600' : trend > 0 ? 'text-red-600' : 'text-gray-500'}>
                  {getTrendLabel(trend, type)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Progress Chart */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Progress Over Time</p>
            <span className="text-xs text-gray-400">{data.length} assessments</span>
          </div>
          <SimpleLineChart 
            data={[...data].reverse()} 
            maxScore={maxScore} 
            zones={zones}
            title={type}
            color={severity.color}
          />
        </div>

        {/* Overall Trend Indicator */}
        {data.length > 2 && (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${isImproving ? 'bg-green-50' : 'bg-amber-50'}`}>
            {isImproving ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">
                  Overall improvement of {Math.abs(overallTrend)} points since first assessment
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-700">
                  Scores have increased by {Math.abs(overallTrend)} points - may need attention
                </span>
              </>
            )}
          </div>
        )}

        {/* Expandable History */}
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide' : 'Show'} assessment history
          </button>
          
          {expanded && (
            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
              {data.map((assessment, idx) => {
                const zone = getSeverityZone(assessment.score, type);
                return (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{new Date(assessment.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{assessment.score}</span>
                      <Badge variant="outline" className={`${zone.bgColor} ${zone.textColor} text-xs`}>
                        {zone.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
const OutcomeVisualization = ({ token, clientId, clientName = 'Client' }) => {
  const [loading, setLoading] = useState(true);
  const [phq9Data, setPhq9Data] = useState([]);
  const [gad7Data, setGad7Data] = useState([]);
  const [timeRange, setTimeRange] = useState('all');

  const fetchAssessments = useCallback(async () => {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/assessments/patient/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const assessments = response.data.data || [];
      
      // Filter and format PHQ-9 data
      const phq9 = assessments
        .filter(a => a.assessment_type?.toUpperCase().includes('PHQ'))
        .map(a => ({
          date: a.assessment_date,
          score: a.total_score || 0,
          severity: a.severity,
          notes: a.notes
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Filter and format GAD-7 data
      const gad7 = assessments
        .filter(a => a.assessment_type?.toUpperCase().includes('GAD'))
        .map(a => ({
          date: a.assessment_date,
          score: a.total_score || 0,
          severity: a.severity,
          notes: a.notes
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Apply time range filter
      const filterByTimeRange = (data) => {
        if (timeRange === 'all') return data;
        const now = new Date();
        const cutoff = new Date();
        if (timeRange === '3m') cutoff.setMonth(now.getMonth() - 3);
        if (timeRange === '6m') cutoff.setMonth(now.getMonth() - 6);
        if (timeRange === '1y') cutoff.setFullYear(now.getFullYear() - 1);
        return data.filter(d => new Date(d.date) >= cutoff);
      };
      
      setPhq9Data(filterByTimeRange(phq9));
      setGad7Data(filterByTimeRange(gad7));
      
    } catch (error) {
      console.error('Error fetching assessments:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  }, [clientId, token, timeRange]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasAnyData = phq9Data.length > 0 || gad7Data.length > 0;

  return (
    <div className="space-y-6" data-testid="outcome-visualization">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Treatment Outcomes
          </h2>
          <p className="text-sm text-gray-500">
            {clientName ? `${clientName}'s` : 'Client'} mental health progress over time
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={fetchAssessments}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      {hasAnyData && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Understanding Your Scores</p>
            <p className="mt-1">
              Lower scores indicate improvement. Green zones are healthy ranges, while orange/red zones may need clinical attention. 
              Track your progress over time to see how treatment is helping.
            </p>
          </div>
        </div>
      )}

      {/* Assessment Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AssessmentCard 
          type="PHQ-9"
          data={phq9Data}
          icon={Brain}
          color="text-purple-600"
          maxScore={27}
          zones={PHQ9_ZONES}
        />
        
        <AssessmentCard 
          type="GAD-7"
          data={gad7Data}
          icon={Heart}
          color="text-rose-600"
          maxScore={21}
          zones={GAD7_ZONES}
        />
      </div>

      {/* No Data State */}
      {!hasAnyData && (
        <Card className="border-dashed">
          <CardContent className="text-center py-12">
            <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Assessments Yet</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              PHQ-9 and GAD-7 assessments will appear here once completed. 
              These screenings help track depression and anxiety symptoms over time.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Severity Legend */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Severity Scale Reference</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-2">PHQ-9 (Depression)</p>
              <div className="flex flex-wrap gap-2">
                {PHQ9_ZONES.map(zone => (
                  <Badge key={zone.label} className={`${zone.bgColor} ${zone.textColor} text-xs`}>
                    {zone.min}-{zone.max}: {zone.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2">GAD-7 (Anxiety)</p>
              <div className="flex flex-wrap gap-2">
                {GAD7_ZONES.map(zone => (
                  <Badge key={zone.label} className={`${zone.bgColor} ${zone.textColor} text-xs`}>
                    {zone.min}-{zone.max}: {zone.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OutcomeVisualization;
